import { useCallback, useEffect, useRef, useState } from "react";

interface FileDropZoneProps {
    files: File[];
    onFilesChange: (files: File[]) => void;
    maxFiles?: number;
    maxSizeMB?: number;
    disabled?: boolean;
}

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileDropZone = ({ files, onFilesChange, maxFiles = 3, maxSizeMB = 10, disabled = false }: FileDropZoneProps) => {
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);
    const dropRef = useRef<HTMLDivElement>(null);
    const dragCounter = useRef(0);

    const validateAndAddFiles = useCallback(
        (newFiles: FileList | File[]) => {
            setError("");
            const fileArray = Array.from(newFiles);
            const maxSizeBytes = maxSizeMB * 1024 * 1024;

            // 이미지 파일만 허용
            const nonImage = fileArray.find(f => !f.type.startsWith("image/"));
            if (nonImage) {
                setError(`이미지 파일만 첨부할 수 있습니다: ${nonImage.name}`);
                return;
            }

            // 파일 개수 체크
            if (files.length + fileArray.length > maxFiles) {
                setError(`사진은 최대 ${maxFiles}장까지 첨부할 수 있습니다.`);
                return;
            }

            // 개별 파일 크기 체크
            const oversized = fileArray.find(f => f.size > maxSizeBytes);
            if (oversized) {
                setError(`파일 크기가 ${maxSizeMB}MB를 초과합니다: ${oversized.name}`);
                return;
            }

            // 중복 파일명 제거
            const existingNames = new Set(files.map(f => f.name));
            const uniqueFiles = fileArray.filter(f => !existingNames.has(f.name));

            if (uniqueFiles.length === 0 && fileArray.length > 0) {
                setError("이미 동일한 파일이 첨부되어 있습니다.");
                return;
            }

            onFilesChange([...files, ...uniqueFiles]);
        },
        [files, maxFiles, maxSizeMB, onFilesChange],
    );

    const handleRemove = useCallback(
        (index: number) => {
            onFilesChange(files.filter((_, i) => i !== index));
            setError("");
        },
        [files, onFilesChange],
    );

    // 드래그앤드롭
    const handleDragEnter = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current++;
            if (!disabled) setIsDragging(true);
        },
        [disabled],
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            dragCounter.current = 0;
            if (!disabled && e.dataTransfer.files.length > 0) {
                validateAndAddFiles(e.dataTransfer.files);
            }
        },
        [disabled, validateAndAddFiles],
    );

    // 클립보드 붙여넣기
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (disabled) return;
            if (!dropRef.current) return;

            // dropzone 영역 내에서만 동작하도록 체크
            const activeEl = document.activeElement;
            if (activeEl && (activeEl.tagName === "INPUT" || activeEl.tagName === "TEXTAREA")) return;

            if (e.clipboardData && e.clipboardData.files.length > 0) {
                e.preventDefault();
                validateAndAddFiles(e.clipboardData.files);
            }
        };

        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [disabled, validateAndAddFiles]);

    // 파일 선택 버튼
    const handleButtonClick = () => {
        if (!disabled) inputRef.current?.click();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            validateAndAddFiles(e.target.files);
            e.target.value = "";
        }
    };

    return (
        <div className="flex flex-col gap-1.5">
            <div
                ref={dropRef}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleButtonClick}
                className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-5 transition-colors cursor-pointer ${
                    disabled
                        ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                        : isDragging
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300 hover:border-gray-400 bg-white"
                }`}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={isDragging ? "#3B82F6" : "#9CA3AF"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p className="text-[15px] text-gray-500 text-center break-keep">
                    픽업할 물품(위치) 사진을 드래그하거나, 클릭하여 첨부하세요
                    <br />
                    <span className="text-[13px] text-gray-400">(이미지 파일만 가능 · Ctrl+V 붙여넣기 가능 · 최대 {maxFiles}장, 각 {maxSizeMB}MB)</span>
                </p>
            </div>

            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleInputChange} />

            {error && <p className="text-[14px] text-[#ff4242]">{error}</p>}

            {files.length > 0 && (
                <div className="flex flex-col gap-1">
                    {files.map((file, index) => (
                        <div
                            key={`${file.name}-${index}`}
                            className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#6B7280"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="flex-shrink-0"
                                >
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                </svg>
                                <span className="text-[14px] text-gray-700 truncate">{file.name}</span>
                                <span className="text-[12px] text-gray-400 flex-shrink-0">{formatFileSize(file.size)}</span>
                            </div>
                            <button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleRemove(index);
                                }}
                                className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#9CA3AF"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileDropZone;
